/**
 * Campaign composer - the five steps from the brief (§8):
 *   1 Type · 2 Compose · 3 Audience · 4 Schedule · 5 Preview & send
 *
 * Two decisions worth stating:
 *
 * - The campaign is SAVED AS A DRAFT at the end of step 2, not at the end of
 *   step 5. Audience preview and test-send both need a real campaign id, and an
 *   operator who loses a witty line because they closed the tab on step 4 will
 *   not write another one.
 *
 * - Sending is gated behind approval, in the UI as well as the API. The final
 *   step offers Approve, then Send - it never offers a single button that does
 *   both, because that is precisely the button that gets clicked by accident.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check, Send, Users } from 'lucide-react'
import { useColors } from '../../hooks/useColors'
import {
  Card, Chip, Field, PageHeader, PillButton,
  SelectInput, TextArea, TextInput,
} from '../../components/shared/primitives'
import NotificationPreview from '../../features/notifications/components/NotificationPreview'
import StepRail from '../../features/notifications/components/StepRail'
import CampaignTypePicker from '../../features/notifications/components/CampaignTypePicker'
import { notificationsApi } from '../../features/notifications/api'
import {
  BODY_SOFT_LIMIT, CAMPAIGN_KINDS, CTA_OPTIONS, DEEPLINK_OPTIONS,
  PRIORITY_LABEL, TITLE_SOFT_LIMIT,
} from '../../features/notifications/constants'
import '../../styles/notifications.css'
import type {
  AudienceDefinition, AudiencePreview, Campaign, Segment, Template,
} from '../../features/notifications/types'

const STEPS = ['Type', 'Compose', 'Audience', 'Schedule', 'Preview'] as const

/** Params an operator can tune per segment type, so the composer is not a JSON editor. */
const SEGMENT_PARAM_HINTS: Record<string, { key: string; label: string; suffix: string }> = {
  new_users: { key: 'days', label: 'Signed up within', suffix: 'days' },
  inactive: { key: 'days', label: 'No order for', suffix: 'days' },
  recent: { key: 'days', label: 'Ordered within', suffix: 'days' },
  lifetime_value: { key: 'min', label: 'Lifetime spend above', suffix: '₹' },
  abandoned_cart: { key: 'hours', label: 'Cart untouched for', suffix: 'hours' },
}

export default function CampaignComposerPage() {
  const colors = useColors()
  const navigate = useNavigate()
  const { id: existingId } = useParams()
  const [searchParams] = useSearchParams()

  const [step, setStep] = useState(existingId ? 1 : 0)
  const [campaignId, setCampaignId] = useState<string | null>(existingId ?? null)
  const [campaign, setCampaign] = useState<Campaign | null>(null)

  const [kindIndex, setKindIndex] = useState(0)
  const [category, setCategory] = useState(CAMPAIGN_KINDS[0].defaultCategory)
  const [name, setName] = useState('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [ctaLabel, setCtaLabel] = useState('')
  const [deeplink, setDeeplink] = useState('')

  const [segments, setSegments] = useState<Segment[]>([])
  const [segmentSlug, setSegmentSlug] = useState('all_users')
  const [segmentParam, setSegmentParam] = useState('')
  const [audiencePreview, setAudiencePreview] = useState<AudiencePreview | null>(null)
  const [previewing, setPreviewing] = useState(false)

  const [scheduleMode, setScheduleMode] = useState<'now' | 'scheduled'>('now')
  const [sendAt, setSendAt] = useState('')
  const [overrideCaps, setOverrideCaps] = useState(false)

  const [busy, setBusy] = useState(false)
  // Synchronous duplicate-send guard. A ref, not state, because state updates a
  // render late and a fast double-click gets through in that gap.
  const sendingRef = useRef(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const kind = CAMPAIGN_KINDS[kindIndex]

  // ── Load an existing campaign (edit) or a template (start from library) ────
  useEffect(() => {
    if (!existingId) return
    void (async () => {
      try {
        const { campaign: loaded } = await notificationsApi.getCampaign(existingId)
        setCampaign(loaded)
        setCategory(loaded.category)
        setName(loaded.name)
        setTitle(loaded.title)
        setBody(loaded.body)
        setImageUrl(loaded.image_url ?? '')
        setCtaLabel(loaded.cta_label ?? '')
        setDeeplink(loaded.deeplink ?? '')
        const definition = loaded.audience || {}
        setSegmentSlug((definition.type as string) || 'all_users')
        const params = definition.params || {}
        const firstParam = Object.values(params)[0]
        setSegmentParam(firstParam === undefined ? '' : String(firstParam))
        setOverrideCaps(Boolean(loaded.override_caps))
        const foundKind = CAMPAIGN_KINDS.findIndex((item) => item.categories.includes(loaded.category))
        if (foundKind >= 0) setKindIndex(foundKind)
      } catch {
        setError('Could not load this campaign')
      }
    })()
  }, [existingId])

  useEffect(() => {
    void (async () => {
      try {
        setSegments(await notificationsApi.listSegments())
      } catch {
        // A failed segment list must not block composing - the default
        // "all users" segment still works.
      }
    })()
  }, [])

  const templateId = searchParams.get('template')
  useEffect(() => {
    if (!templateId || existingId) return
    void (async () => {
      const templates = await notificationsApi.listTemplates()
      const template = templates.find((item: Template) => item.id === templateId)
      if (!template) return
      setCategory(template.category)
      setName(template.name)
      setTitle(template.title)
      setBody(template.body)
      setCtaLabel(template.cta_label ?? '')
      setDeeplink(template.deeplink ?? '')
      setImageUrl(template.image_url ?? '')
      const foundKind = CAMPAIGN_KINDS.findIndex((item) => item.categories.includes(template.category))
      if (foundKind >= 0) setKindIndex(foundKind)
      setStep(1)
    })()
  }, [templateId, existingId])

  const audienceDefinition = useMemo<AudienceDefinition>(() => {
    const hint = SEGMENT_PARAM_HINTS[segmentSlug]
    if (!hint || segmentParam === '') return { type: segmentSlug }
    return { type: segmentSlug, params: { [hint.key]: Number(segmentParam) } }
  }, [segmentSlug, segmentParam])

  /** Save on leaving step 2 so audience preview and test-send have a real id. */
  const persistDraft = useCallback(async () => {
    const payload = {
      name: name.trim() || title.trim().slice(0, 60),
      category,
      title: title.trim(),
      body: body.trim(),
      image_url: imageUrl.trim() || null,
      cta_label: ctaLabel || null,
      deeplink: deeplink || null,
      audience: audienceDefinition,
      override_caps: overrideCaps,
    }
    if (campaignId) {
      const updated = await notificationsApi.updateCampaign(campaignId, payload as Partial<Campaign>)
      setCampaign(updated)
      return updated
    }
    const created = await notificationsApi.createCampaign(payload as Partial<Campaign>)
    setCampaignId(created.id)
    setCampaign(created)
    return created
  }, [campaignId, name, category, title, body, imageUrl, ctaLabel, deeplink, audienceDefinition, overrideCaps])

  const runPreview = useCallback(async () => {
    setPreviewing(true)
    setError('')
    try {
      setAudiencePreview(await notificationsApi.previewAudience(audienceDefinition))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Audience preview failed')
    } finally {
      setPreviewing(false)
    }
  }, [audienceDefinition])

  useEffect(() => {
    if (step === 2) void runPreview()
  }, [step, runPreview])

  const guard = async (action: () => Promise<void>) => {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await action()
    } catch (err) {
      const response = (err as { response?: { data?: { message?: string } } }).response
      setError(response?.data?.message || (err instanceof Error ? err.message : 'Something went wrong'))
    } finally {
      setBusy(false)
    }
  }

  const goNext = () => guard(async () => {
    if (step === 1) await persistDraft()
    if (step === 3) await persistDraft()
    setStep((current) => Math.min(current + 1, STEPS.length - 1))
  })

  const handleApprove = () => guard(async () => {
    if (!campaignId) return
    const approved = await notificationsApi.approveCampaign(campaignId)
    setCampaign(approved)
    setNotice('Approved. It can now be scheduled.')
  })

  const handleSend = () => guard(async () => {
    if (!campaignId) return
    // Guard the double-click before the request goes out, not after: `busy` only
    // flips once guard() runs, and two fast clicks both pass that check.
    if (sendingRef.current) return
    sendingRef.current = true
    const when = scheduleMode === 'scheduled' && sendAt ? new Date(sendAt).toISOString() : null
    try {
      const result = await notificationsApi.scheduleCampaign(campaignId, when)
      setCampaign(result.campaign)
      setNotice(result.message)
      // Sending has begun on the server; the detail page is where progress lives.
      if (!when) navigate(`/notifications/${campaignId}`)
    } finally {
      sendingRef.current = false
    }
  })

  const handleTestSend = () => guard(async () => {
    if (!campaignId) await persistDraft()
    const target = campaignId ?? (await persistDraft()).id
    const result = await notificationsApi.testSendCampaign(target)
    setNotice(
      result.sent > 0
        ? 'Test push sent to your own device.'
        : 'Test not delivered. Your admin account has no active app device registered.'
    )
  })

  const canAdvance = step === 1 ? Boolean(title.trim() && body.trim()) : true
  const approved = campaign?.status === 'approved'
  const dispatched = campaign ? ['scheduled', 'sending', 'sent'].includes(campaign.status) : false

  return (
    <div>
      <PageHeader
        title={existingId ? 'Edit campaign' : 'New campaign'}
        subtitle={name.trim() || 'Untitled draft'}
        actions={(
          <PillButton variant="ghost" onClick={() => navigate('/notifications')}>
            <ArrowLeft size={14} /> Notification Center
          </PillButton>
        )}
      />

      <StepRail
        steps={STEPS}
        current={step}
        unlocked={Boolean(campaignId)}
        onSelect={setStep}
      />

      {error ? (
        <Card style={{ marginBottom: '16px', borderColor: '#B45309' }}>
          <span style={{ color: '#B45309' }}>{error}</span>
        </Card>
      ) : null}
      {notice ? (
        <Card style={{ marginBottom: '16px', borderColor: '#15803D' }}>
          <span style={{ color: '#15803D' }}>{notice}</span>
        </Card>
      ) : null}

      <div className="ntf-split">
        <div style={{ display: 'grid', gap: '18px' }}>
          {/* ── Step 1: type ─────────────────────────────────────────────── */}
          {step === 0 ? (
            <section className="ntf-panel">
              <header className="ntf-panel__head">
                <div>
                  <div className="ntf-eyebrow">Step one</div>
                  <h2 className="font-display ntf-panel__title">What kind of notification is this?</h2>
                  <p className="ntf-panel__hint">
                    The kind sets the priority lane and decides which preference switch a
                    customer can use to mute it. P2 rides above P3, and P4 is the first thing
                    dropped when someone is near their cap.
                  </p>
                </div>
              </header>
              <div className="ntf-panel__body">
                <CampaignTypePicker
                  kinds={CAMPAIGN_KINDS}
                  selectedKey={kind.key}
                  onSelect={(next) => {
                    setKindIndex(CAMPAIGN_KINDS.findIndex((item) => item.key === next.key))
                    setCategory(next.defaultCategory)
                  }}
                />

                <div style={{ marginTop: '18px', maxWidth: '340px' }}>
                  <Field
                    label="Exact category"
                    hint="Narrows the kind. Sets the lane and which preference switch mutes it."
                  >
                    <SelectInput value={category} onChange={(event) => setCategory(event.target.value)}>
                      {kind.categories.map((value) => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </SelectInput>
                  </Field>
                </div>
              </div>
            </section>
          ) : null}

          {/* ── Step 2: compose ──────────────────────────────────────────── */}
          {step === 1 ? (
            <section className="ntf-panel">
              <header className="ntf-panel__head">
                <div>
                  <div className="ntf-eyebrow">Step two</div>
                  <h2 className="font-display ntf-panel__title">Write it</h2>
                  <p className="ntf-panel__hint">Short, Indian, playful. The previews on the right show the real truncation on each platform. Write to what you can actually see there, not to the character count.</p>
                </div>
                
              </header>
              <div className="ntf-panel__body">
                <div style={{ display: 'grid', gap: '14px' }}>
                <Field label="Campaign name" hint="Internal only. Never shown to a customer.">
                  <TextInput value={name} onChange={(event) => setName(event.target.value)} placeholder="Saturday meme drop" />
                </Field>
                <Field
                  label="Notification title"
                  hint={title.length > TITLE_SOFT_LIMIT ? `${title.length} characters. Lock screens truncate around ${TITLE_SOFT_LIMIT}` : `${title.length} characters`}
                >
                  <TextInput value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Your chair isn't a wardrobe 😭" maxLength={150} />
                </Field>
                <Field
                  label="Body"
                  hint={body.length > BODY_SOFT_LIMIT ? `${body.length} characters. Long bodies get collapsed` : `${body.length} characters`}
                >
                  <TextArea rows={3} value={body} onChange={(event) => setBody(event.target.value)} placeholder="Send that pile to Rapidry before the chair gives up." />
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <Field label="Call to action">
                    <SelectInput value={ctaLabel} onChange={(event) => setCtaLabel(event.target.value)}>
                      <option value="">None</option>
                      {CTA_OPTIONS.map((value) => <option key={value} value={value}>{value}</option>)}
                    </SelectInput>
                  </Field>
                  <Field label="Opens" hint="Tapping lands here. Never the generic home screen when a real destination exists.">
                    <SelectInput value={deeplink} onChange={(event) => setDeeplink(event.target.value)}>
                      {DEEPLINK_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </SelectInput>
                  </Field>
                </div>
                <Field label="Image URL (optional)" hint="The copy must still read correctly without it. Data-saver and the in-app inbox both drop images.">
                  <TextInput value={imageUrl} onChange={(event) => setImageUrl(event.target.value)} placeholder="https://…" />
                </Field>
              </div>
              </div>
            </section>
          ) : null}

          {/* ── Step 3: audience ─────────────────────────────────────────── */}
          {step === 2 ? (
            <section className="ntf-panel">
              <header className="ntf-panel__head">
                <div>
                  <div className="ntf-eyebrow">Step three</div>
                  <h2 className="font-display ntf-panel__title">Who gets it</h2>
                  <p className="ntf-panel__hint">This is the pool the campaign draws from. Caps, quiet hours and opt-outs are applied at send time, so the number actually pushed will be lower.</p>
                </div>
                <PillButton variant="secondary" onClick={() => void runPreview()} disabled={previewing}>Recount</PillButton>
              </header>
              <div className="ntf-panel__body">
                <div style={{ display: 'grid', gap: '14px', maxWidth: '480px' }}>
                <Field label="Segment">
                  <SelectInput
                    value={segmentSlug}
                    onChange={(event) => {
                      setSegmentSlug(event.target.value)
                      setSegmentParam('')
                    }}
                  >
                    {segments.length === 0 ? <option value="all_users">All users</option> : null}
                    {segments.map((segment) => {
                      const definition = typeof segment.definition === 'string'
                        ? JSON.parse(segment.definition)
                        : segment.definition
                      return (
                        <option key={segment.id} value={definition.type as string}>
                          {segment.name}
                        </option>
                      )
                    })}
                  </SelectInput>
                </Field>

                {SEGMENT_PARAM_HINTS[segmentSlug] ? (
                  <Field label={SEGMENT_PARAM_HINTS[segmentSlug].label}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <TextInput
                        type="number"
                        value={segmentParam}
                        onChange={(event) => setSegmentParam(event.target.value)}
                        style={{ width: '120px' }}
                      />
                      <span style={{ color: colors.textMuted, fontSize: '13px' }}>
                        {SEGMENT_PARAM_HINTS[segmentSlug].suffix}
                      </span>
                    </div>
                  </Field>
                ) : null}

                <div
                  style={{
                    background: colors.surface,
                    borderRadius: '14px',
                    padding: '16px',
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Users size={16} color={colors.textMuted} />
                    <strong style={{ fontSize: '20px' }}>
                      {previewing ? '…' : (audiencePreview?.size ?? 0).toLocaleString('en-IN')}
                    </strong>
                    <span style={{ color: colors.textMuted, fontSize: '13px' }}>customers match</span>
                  </div>
                  {audiencePreview?.reachability ? (
                    <div style={{ marginTop: '14px', borderTop: `1px solid ${colors.border}`, paddingTop: '12px' }}>
                      <div className="ntf-eyebrow" style={{ marginBottom: '8px' }}>
                        Can actually be reached
                      </div>
                      <div className="ntf-row">
                        <span style={{ color: colors.textSecondary }}>Reachable by push</span>
                        <span className="ntf-row__value" style={{ color: '#15803D' }}>
                          {audiencePreview.reachability.reachable.toLocaleString('en-IN')}
                        </span>
                      </div>
                      {([
                        ['permission_denied', 'Notifications turned off on their phone'],
                        ['token_invalid', 'App reinstalled or token expired'],
                        // "no user_devices row" ≠ "never installed" - every such
                        // customer signed up in-app; their version predates
                        // launch-time device reporting (added in v1.3).
                        ['no_device', 'App has not reported for push yet (opens on an older version)'],
                      ] as const).map(([key, label]) => {
                        const count = audiencePreview.reachability?.[key] ?? 0
                        if (count === 0) return null
                        return (
                          <div key={key} className="ntf-row">
                            <span style={{ color: colors.textMuted }}>{label}</span>
                            <span className="ntf-row__value" style={{ color: colors.textMuted }}>
                              {count.toLocaleString('en-IN')}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  ) : null}

                  <p style={{ margin: '10px 0 0', fontSize: '12px', color: colors.textMuted, lineHeight: 1.5 }}>
                    Only reachable customers receive a push. Frequency caps, quiet hours and
                    per-category opt-outs are applied on top at send time, so the number actually
                    pushed can be lower again.
                  </p>
                  {audiencePreview?.sample?.length ? (
                    <div style={{ marginTop: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {audiencePreview.sample.map((person) => (
                        <Chip key={person.id}>{person.name}</Chip>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
              </div>
            </section>
          ) : null}

          {/* ── Step 4: schedule ─────────────────────────────────────────── */}
          {step === 3 ? (
            <section className="ntf-panel">
              <header className="ntf-panel__head">
                <div>
                  <div className="ntf-eyebrow">Step four</div>
                  <h2 className="font-display ntf-panel__title">When it goes out</h2>
                  <p className="ntf-panel__hint">A campaign due inside quiet hours is held until the window opens, never dropped.</p>
                </div>
                
              </header>
              <div className="ntf-panel__body">
                <div style={{ display: 'grid', gap: '14px', maxWidth: '420px' }}>
                <Field label="Timing">
                  <SelectInput value={scheduleMode} onChange={(event) => setScheduleMode(event.target.value as 'now' | 'scheduled')}>
                    <option value="now">Send as soon as it is approved</option>
                    <option value="scheduled">Schedule for a specific time</option>
                  </SelectInput>
                </Field>
                {scheduleMode === 'scheduled' ? (
                  <Field label="Send at" hint="Local time. A campaign due inside quiet hours is held until the window opens, not dropped.">
                    <TextInput type="datetime-local" value={sendAt} onChange={(event) => setSendAt(event.target.value)} />
                  </Field>
                ) : null}
                <div style={{ fontSize: '12px', color: colors.textMuted, lineHeight: 1.6 }}>
                  Priority <strong>{PRIORITY_LABEL[(campaign?.priority ?? 3) as 2 | 3 | 4]}</strong>.
                  If a recipient received an order update in the last half hour, this waits.
                  Order communication is never pushed aside by marketing.
                </div>

                <label
                  style={{
                    display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer',
                    border: `1px solid ${overrideCaps ? colors.textPrimary : colors.border}`,
                    background: overrideCaps ? colors.surface : colors.bgCard,
                    borderRadius: '10px', padding: '12px 14px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={overrideCaps}
                    onChange={(event) => setOverrideCaps(event.target.checked)}
                    style={{ marginTop: '2px' }}
                  />
                  <span>
                    <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                      Ignore the frequency caps for this campaign
                    </span>
                    <span style={{ display: 'block', fontSize: '12px', color: colors.textMuted, marginTop: '3px', lineHeight: 1.5 }}>
                      Sends even to customers who have already had a campaign today. Use it for
                      something that genuinely cannot wait. It does not override a customer who
                      turned this category off.
                    </span>
                  </span>
                </label>
              </div>
              </div>
            </section>
          ) : null}

          {/* ── Step 5: preview & send ───────────────────────────────────── */}
          {step === 4 ? (
            <section className="ntf-panel">
              <header className="ntf-panel__head">
                <div>
                  <div className="ntf-eyebrow">Step five</div>
                  <h2 className="font-display ntf-panel__title">Ready to send</h2>
                  <p className="ntf-panel__hint">Approval and sending are separate on purpose. Approving records who signed the copy off; sending is the irreversible step.</p>
                </div>
                
              </header>
              <div className="ntf-panel__body">
                <div style={{ display: 'grid', gap: '10px', fontSize: '13px', color: colors.textSecondary }}>
                <div><strong>{name || title}</strong> · {category}</div>
                <div>Audience: {(audiencePreview?.size ?? campaign?.audience_size ?? 0).toLocaleString('en-IN')} customers</div>
                <div>Opens: {deeplink || 'the app'}</div>
                <div>
                  Status: <Chip tone={approved ? 'info' : 'neutral'}>{campaign?.status ?? 'draft'}</Chip>
                </div>
                {overrideCaps ? (
                  <div>
                    <Chip tone="warning">Frequency caps ignored</Chip>
                  </div>
                ) : null}
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '18px', flexWrap: 'wrap' }}>
                <PillButton variant="secondary" onClick={handleTestSend} disabled={busy}>
                  Send a test to me
                </PillButton>
                {!approved && !dispatched ? (
                  <PillButton variant="outline" onClick={handleApprove} disabled={busy}>
                    <Check size={14} /> Approve
                  </PillButton>
                ) : null}
                <PillButton onClick={handleSend} disabled={busy || !approved || dispatched}>
                  <Send size={14} />
                  {busy
                    ? 'Starting…'
                    : scheduleMode === 'scheduled'
                      ? 'Schedule'
                      : `Send to ${(audiencePreview?.reachability?.reachable ?? audiencePreview?.size ?? 0).toLocaleString('en-IN')}`}
                </PillButton>
              </div>
              {!approved && !dispatched ? (
                <p style={{ margin: '12px 0 0', fontSize: '12px', color: colors.textMuted }}>
                  Only an approved campaign can be sent. Approving records who signed the copy off.
                </p>
              ) : null}
              {dispatched ? (
                <p style={{ margin: '12px 0 0', fontSize: '12px', color: colors.textMuted }}>
                  This campaign is already on its way.{' '}
                  <button
                    type="button"
                    onClick={() => navigate(`/notifications/${campaignId}`)}
                    style={{ background: 'none', border: 'none', padding: 0, color: colors.textPrimary, textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Watch it live
                  </button>
                </p>
              ) : null}
              </div>
            </section>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PillButton variant="ghost" onClick={() => setStep((current) => Math.max(0, current - 1))} disabled={step === 0}>
              <ArrowLeft size={14} /> Back
            </PillButton>
            {step < STEPS.length - 1 ? (
              <PillButton onClick={goNext} disabled={busy || !canAdvance}>
                Next <ArrowRight size={14} />
              </PillButton>
            ) : null}
          </div>
        </div>

        {/* Live previews, visible at every step. */}
        <div className="ntf-rail">
          <NotificationPreview platform="android" title={title} body={body} imageUrl={imageUrl} ctaLabel={ctaLabel} />
          <NotificationPreview platform="ios" title={title} body={body} imageUrl={imageUrl} ctaLabel={ctaLabel} />
        </div>
      </div>
    </div>
  )
}
